/**
 * Attachments: the files that go out with every message from this template —
 * inside each one where this deployment attaches them, as links under the footer
 * where it publishes them. `AttachmentDeliveryNotice` says which, on screen.
 *
 * **Not blocks, and the difference is the whole design of this panel.** An
 * inline image is referenced by an image block, appears in the body, and is
 * swept away when no block references it. An attachment is referenced by
 * nothing — it hangs off the template — which has two consequences a person can
 * see:
 *
 *   1. it does not appear anywhere in the preview, because it is not in the
 *      email's *body*. The panel says so, because a file you uploaded and then
 *      cannot find in the preview reads as a bug;
 *   2. the only way one comes off is this Remove button. There is no block to
 *      delete, so there is nothing for a save to notice.
 *
 * ## THE RETRACTION THIS PANEL CARRIED WAS ITSELF FALSE, AND SLICE 17 FOUND IT
 *
 * The paragraph that stood here said: *attachments cannot be sent yet. SES v2's
 * simple content has no attachment field, so carrying one needs the raw-MIME
 * send path that arrives with campaigns in slice 5.* Both halves were true when
 * written and neither is true now. `core/send/raw-message.ts` builds **raw MIME
 * always** — explicitly not "simple when there are no attachments, raw when
 * there are", because two send paths mean one of them is under-tested — and
 * `sending/message-source.ts` reads every `kind === 'attachment'` asset and
 * hands it to that builder as `attachments`. A campaign has carried them since
 * that landed.
 *
 * So the screen told a tenant that a working feature does not work, in a
 * `Callout`, above the list of files it was talking about. **That is worse than
 * the verbose prose this slice exists to cut**, and it is the exact shape the
 * repo's rule about stale justifications describes: a comment whose premise has
 * been broken does not merely go out of date, it argues the next reader into
 * something wrong. Here it argued a tenant into attaching their price list
 * somewhere else.
 *
 * The panel now claims only what is still true and still load-bearing: **an
 * attachment is not part of the body, so it is not in the preview.** A file
 * somebody uploaded and then cannot find in the preview reads as a bug, and that
 * is the false conclusion this callout exists to prevent. How the bytes actually
 * travel, and the fact that Amazon's own message-size limit covers body, inline
 * images and attachments together, folds behind `MoreDetail`.
 *
 * **This was not found by reading the screen.** It was found by tracing the
 * claim into `apps/api` during the adversarial pass, which is the only way a
 * sentence like this is ever caught — it reads perfectly, it typechecks, and
 * every test in the repository passes with it standing.
 *
 * ## Slice 17 — the rest
 *
 * **The preview claim stays a `Callout` and stays above the list**, in the slot
 * the false one occupied, because it is the same shape of mistake pointed the
 * right way: somebody uploads a file, goes looking for it in the preview, and
 * concludes something is broken. Only the mechanism — raw MIME, and whose size
 * limit the budget is measured against — folded into `MoreDetail`.
 *
 * **The limits did not fold.** The counter in the header and the per-file
 * ceiling in the paragraph are the whole budget, and a budget discovered by
 * being refused is a budget that was not shown.
 *
 * ## Slice 23: the panel now says how the files travel, because it can differ
 *
 * The saying itself is `AttachmentDeliveryNotice`, in its own file, because this
 * one imports the API client and therefore cannot be rendered by a suite — so
 * every claim made here is provable only as source text, which cannot tell a
 * sentence that is in the file from one that reaches the screen. The notice is
 * pure copy and is rendered in both modes by `verify-template-images`.
 *
 * A deployment with an asset host **links** to a file instead of attaching it:
 * the file is published once and the message carries a link under the footer.
 * The author has to know which, before they schedule anything, because it is the
 * reader's experience that changes — no paperclip, nothing saved offline, a link
 * rather than a file when the mail is forwarded. That is slice 23's third
 * done-when, and it is `TEMPLATE_ATTACHMENT_DELIVERY_SUMMARY` and its `_DETAIL`
 * in `@wend/shared`, said here where the file is chosen rather than at schedule
 * time, where it would be too late to choose differently.
 *
 * **The deliverability callout is gated on the mode**, and that is the point of
 * the gate rather than a tidy-up: `attachmentDeliverabilityMessage` is about a
 * filter's treatment of an attached file, and where files are linked there is no
 * attachment in the message at all. Leaving it standing would be this panel's
 * own historical mistake repeated — a sentence whose premise a later change
 * broke, arguing a tenant out of sending a file that now costs them nothing.
 *
 * ## What hosted images added here, and what they did not
 *
 * **One sentence, and it was the only attachment change in that slice.** Slice 21
 * held that an attachment is not hosted, does not get a public id, and that the
 * database refuses one — a file the recipient was meant to be handed being a
 * different product decision from a picture in the body, with a different
 * failure mode (a link that expires, a file missing from an archived mailbox).
 * Slice 23 is that decision, taken; `0024` widened the CHECK. The failure modes
 * are not denied — the second is real and unanswerable, which is why the mode is
 * a deployment's choice and why this panel states it. What was added then is the
 * `Callout` below the list: `attachmentDeliverabilityMessage`, the same sentence
 * `templateApprovalIssues` produces as advice, said **here** because the
 * approval issues are in the preview tab and the attach button is in this one.
 * Somebody who never opens the preview would otherwise meet it after the
 * campaign had run.
 *
 * It is also why the "How they are sent" paragraph changed, and why it now has
 * two versions. It used to say the whole message — "body, inline images and
 * attachments" — counts towards Amazon's size limit, unconditionally. Where this
 * deployment hosts pictures that is false: they are fetched from a URL when the
 * message is opened, and a person told otherwise would delete a picture to make
 * room for a PDF. Where it embeds them — no image host configured, which is the
 * default and what the live deployment sends — it is exactly true, and a person
 * told pictures are free would attach a PDF beside a half-megabyte banner. So the
 * panel is told `imageDelivery` and says the sentence that is true here.
 *
 * **Three states were missing and are now here.** An empty list said nothing at
 * all — a gap between a paragraph and a button, which reads as a load that came
 * back short rather than as a template with no attachments. Remove said nothing
 * about what it destroys. And the one error state carried the *upload*'s heading
 * over a failed removal, which sends a person looking for a file problem that is
 * not there; it is now a `FormAlert` — this panel's verdict on something just
 * submitted, never a screen that failed to load — with the heading of whichever
 * action failed and the request id beside it.
 */
import {
  MAX_ATTACHMENT_BYTES,
  MAX_TEMPLATE_ATTACHMENTS,
  MAX_TEMPLATE_ATTACHMENTS_TOTAL_BYTES,
  TEMPLATE_ATTACHMENT_ACCEPT,
  attachmentDeliverabilityMessage,
  attachmentDeliveryFor,
  type TemplateAsset,
  type TemplateImageDelivery,
} from '@wend/shared';
import { Button, Callout, Card, CardContent, formatFileSize } from '@wend/ui';
import { FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { FormAlert } from '../../components/FormAlert';
import { AttachmentDeliveryNotice } from './AttachmentDeliveryNotice';
import { errorMessage, isApiError } from '../../lib/api/errors';
import { deleteTemplateAsset, uploadTemplateAsset } from '../../lib/api/templates';
import { MoreDetail } from './MoreDetail';

export interface AttachmentsPanelProps {
  templateId: string;
  /** Every asset on the template; this panel shows only the attachments. */
  assets: readonly TemplateAsset[];
  /**
   * How this deployment carries pictures, from the template view. Decides one
   * clause: whether pictures in the body count towards the message size. Null
   * (a non-email template, never shown here) says neither.
   */
  imageDelivery: TemplateImageDelivery | null;
  disabled: boolean;
  onUploaded: (asset: TemplateAsset) => void;
  onRemoved: (assetId: string) => void;
}

export function AttachmentsPanel({
  templateId,
  assets,
  imageDelivery,
  disabled,
  onUploaded,
  onRemoved,
}: AttachmentsPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{
    title: string;
    message: string;
    requestId: string | undefined;
  } | null>(null);

  /*
   * Derived from the one mode the API sends, never from a second field: a panel
   * that decided for itself whether files are linked is exactly how the editor
   * comes to promise one thing while the send path does another.
   *
   * Null only on a template that is not email, which never renders this panel.
   */
  const attachmentDelivery = imageDelivery === null ? null : attachmentDeliveryFor(imageDelivery);

  const attachments = assets.filter((asset) => asset.kind === 'attachment');
  const usedBytes = attachments.reduce((total, asset) => total + (asset.byteSize ?? 0), 0);
  const atFileLimit = attachments.length >= MAX_TEMPLATE_ATTACHMENTS;
  const remainingBytes = Math.max(0, MAX_TEMPLATE_ATTACHMENTS_TOTAL_BYTES - usedBytes);

  async function upload(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onUploaded(await uploadTemplateAsset(templateId, file, 'attachment'));
    } catch (cause) {
      setError({
        title: 'That file was not attached.',
        message: errorMessage(cause),
        requestId: isApiError(cause) ? cause.requestId : undefined,
      });
    } finally {
      setBusy(false);
      // Cleared so the same file can be picked again after a failure — without
      // this, re-selecting an identical path fires no change event.
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  }

  async function remove(asset: TemplateAsset): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteTemplateAsset(templateId, asset.id);
      onRemoved(asset.id);
    } catch (cause) {
      setError({
        title: 'That file was not removed.',
        message: errorMessage(cause),
        requestId: isApiError(cause) ? cause.requestId : undefined,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="flex items-center gap-2 text-[0.95rem] text-text">
            <Paperclip aria-hidden="true" className="size-4 text-muted" />
            Attachments
          </h2>
          <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
            {attachments.length} of {MAX_TEMPLATE_ATTACHMENTS} · {formatFileSize(usedBytes)} of{' '}
            {formatFileSize(MAX_TEMPLATE_ATTACHMENTS_TOTAL_BYTES)}
          </p>
        </div>

        {/*
          The limits stay on the screen rather than folding: a file budget a
          person discovers by being refused is a budget that was not shown, and
          the counter above plus the per-file ceiling here are the whole of it.
          What folded is *why* they do not appear in the preview.
        */}
        {/*
          MODE-AWARE, because the unconditional version of this sentence — "Files
          sent with every message from this template" — is false on a deployment
          that links to them, and it sat three elements above
          `TEMPLATE_ATTACHMENT_DELIVERY_SUMMARY.linked` saying the opposite. One
          card, two claims, the false one larger: exactly the defect this panel's
          header describes itself as having fixed once already.

          The types and the ceiling do not move with the mode and are said once,
          here, which is the only place either appears.
        */}
        <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
          {attachmentDelivery === 'linked'
            ? 'Files published by Wend and linked from every message sent from this template.'
            : 'Files sent with every message from this template.'}{' '}
          PDF, Word, Excel, PowerPoint, CSV, plain text and images, up to{' '}
          {formatFileSize(MAX_ATTACHMENT_BYTES)} each.
        </p>

        {/*
          THE SENTENCE THAT USED TO BE HERE WAS FALSE — it said attachments
          cannot be sent, long after they could. What replaced it, and what this
          panel still owes a reader, is why an uploaded file is nowhere in the
          preview. Slice 23 moved that sentence and the mode summary beside it
          into `AttachmentDeliveryNotice`, which a suite can render; the argument
          for both lives in that file's header rather than being repeated here.
        */}
        <AttachmentDeliveryNotice imageDelivery={imageDelivery} />

        {/*
          THE ONE SENTENCE ABOUT WHAT AN ATTACHMENT COSTS, SAID WHERE THE FILE IS
          CHOSEN.

          `templateApprovalIssues` already produces this as advice, and the
          preview tab already lists it — but the button that attaches a file is
          on the *edit* tab, and a person who never opens the preview would read
          it for the first time after the campaign had gone. Both call
          `attachmentDeliverabilityMessage`, so there is one sentence rather than
          two accounts of the same trade-off that can drift apart.

          Shown only when there is at least one attachment: a template with none
          has nothing to warn about, and a caution printed over an empty list is
          a caution people learn to scroll past. `tone="info"` because nothing is
          refused here — Wend does not overrule a customer about their own mail.

          AND ONLY WHERE THE FILES ARE ATTACHED. The sentence is about what a
          spam filter does with an attachment; on a deployment that links to
          files there is no attachment in the message, so it would be a cost that
          does not exist. `templateApprovalIssues` applies the same condition, so
          the preview tab and this one agree — and `!== 'linked'` in both, so a
          mode nobody has said anything about still shows the caution.
        */}
        {attachments.length > 0 && attachmentDelivery !== 'linked' && (
          <Callout tone="info" title="An attachment costs something">
            {attachmentDeliverabilityMessage(attachments.length)}
          </Callout>
        )}

        {/*
          A real empty state rather than a gap between a paragraph and a button.
          "No rows" is not the point — what a reader needs is that the absence is
          the normal state of a new template and not a load that came back short.
        */}
        {attachments.length === 0 && (
          <p className="text-[0.88rem] text-muted">
            No files attached. Nothing travels with this template&rsquo;s messages.
          </p>
        )}

        {attachments.length > 0 && (
          <ul className="flex list-none flex-col gap-2">
            {attachments.map((asset) => (
              <li
                key={asset.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-base border border-line px-3 py-2.5"
              >
                <FileText aria-hidden="true" className="size-4 shrink-0 text-muted" />
                <span className="min-w-0 flex-1 truncate text-[0.88rem] text-text">
                  {asset.filename ?? 'Unnamed file'}
                </span>
                <span className="font-mono text-[0.7rem] tabular-nums text-muted">
                  {asset.byteSize === null ? '—' : formatFileSize(asset.byteSize)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={disabled || busy}
                  onClick={() => {
                    void remove(asset);
                  }}
                >
                  <Trash2 aria-hidden="true" />
                  {/* Named, not just an icon: with six files on screen, six
                      identical "Remove" buttons are operable and useless. */}
                  <span className="sr-only">
                    Remove attachment {asset.filename ?? 'unnamed file'}
                  </span>
                  <span aria-hidden="true">Remove</span>
                </Button>
              </li>
            ))}
          </ul>
        )}

        {/*
          What Remove destroys, said beside the buttons rather than behind them.
          There is no confirmation step and there deliberately is not one: the
          loss is a file the person still has on their own machine, which is the
          case where a second click teaches people to click through the third.
          What it is not is recoverable from Wend, and that is the part stated.
        */}
        {attachments.length > 0 && (
          <p className="text-[0.82rem] text-muted">
            Remove deletes the stored file. It cannot be undone from here — the file has to be
            uploaded again.
          </p>
        )}

        {/*
          One state, two possible actions, so the heading follows the action that
          failed: "That file was not attached" over a failed *removal* sends a
          person looking for a file problem that is not there. Same fix
          `WaHeaderMediaPanel` made in slice 9, applied here in slice 17.
        */}
        {error !== null && (
          <FormAlert
            tone="error"
            {...(error.requestId === undefined ? {} : { requestId: error.requestId })}
          >
            {error.title} {error.message}
          </FormAlert>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            accept={TEMPLATE_ATTACHMENT_ACCEPT}
            disabled={disabled || busy || atFileLimit}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) void upload(file);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled || busy || atFileLimit}
            onClick={() => {
              inputRef.current?.click();
            }}
          >
            <Upload aria-hidden="true" />
            {busy ? 'Uploading…' : 'Add a file'}
          </Button>

          {/* A disabled control with no explanation reads as a bug, so both
              limits say which one has been reached. */}
          {atFileLimit ? (
            <p className="text-[0.82rem] text-warn">
              {MAX_TEMPLATE_ATTACHMENTS} attachments is the limit. Remove one to add another.
            </p>
          ) : (
            <p className="text-[0.82rem] text-muted">
              {formatFileSize(remainingBytes)} left in this template&rsquo;s budget.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
